import { useEffect, useState } from 'react';
import { Braces } from 'lucide-react';
import { Field, VariableMenu, inputClass, textareaClass } from '@/components/workflow/inspectorFields';

/**
 * The Tool node's arguments, as a form built from the tool's own parameter schema.
 *
 * What it replaces: one `Arguments (JSON)` textarea for every tool, where the author had to
 * know the parameter names and types, remember which were required, and quote the templates
 * correctly — with the first feedback arriving at RUN time as "Tool arguments are not valid
 * JSON after substitution". The schema that answers all of those questions was already on
 * the tool; routes/workflowsRouter.js now serializes it (services/tools/toolSchema.js) and
 * this renders it.
 *
 * The stored value does not change: `arguments_json` is still a JSON string with `{{…}}`
 * templates inside it, because that is exactly what the engine's `tool` node renders and
 * parses. This is a different EDITOR over the same document, which is what keeps a graph
 * authored before this existed (or by the Workflow Architect agent, or by hand) loadable.
 *
 * Raw JSON stays one click away, and is forced — not merely offered — whenever the form
 * cannot faithfully round-trip what is stored. Silently rewriting an author's arguments
 * into something the form can express would be the worst possible failure here.
 */

/** Values the row editor cannot round-trip force raw mode, the same rule (and the same
 *  reason) as NodeInspector's output-schema editor: an editor that quietly drops what it
 *  does not understand is worse than one that admits it. */
function parseArguments(raw) {
  const text = raw === undefined || raw === null || raw === '' ? '{}' : String(raw);
  try {
    const parsed = JSON.parse(text);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return { args: null, advanced: true };
    return { args: parsed, advanced: false };
  } catch {
    // Unparsable is the NORMAL state mid-typing in raw mode, and also what a template
    // substitution error looks like before the run — either way, hands off.
    return { args: null, advanced: true };
  }
}

/** True when the stored object holds something no widget represents — a nested object, an
 *  array of objects — so the form would lose it on the next keystroke. */
function hasUnrenderableValue(args, fields) {
  const byName = new Map((fields || []).map((f) => [f.name, f]));
  return Object.entries(args).some(([key, value]) => {
    const field = byName.get(key);
    if (!field || !field.renderable) return true;
    if (value === null || value === undefined) return false;
    if (Array.isArray(value)) return value.some((v) => v !== null && typeof v === 'object');
    return typeof value === 'object';
  });
}

/** A template (`{{node.output}}`) is a string wherever it appears, including in a number
 *  or boolean slot — it is substituted before the engine parses the JSON. So a value that
 *  looks like a template is always edited as text. */
function isTemplate(value) {
  return typeof value === 'string' && value.includes('{{');
}

/** Numbers round-trip through a text input so a template can be typed into a numeric
 *  parameter. A clean number is stored as a number; anything else stays a string, which
 *  is what makes `{"topK": "{{input.k}}"}` expressible at all. */
function coerceNumber(text) {
  const trimmed = String(text).trim();
  if (trimmed === '') return undefined;
  if (isTemplate(trimmed)) return trimmed;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : trimmed;
}

function EnumInput({ field, value, onChange }) {
  return (
    <select className={inputClass} value={value ?? ''} onChange={(e) => onChange(e.target.value || undefined)}>
      {!field.required && <option value="">(not set)</option>}
      {field.enum.map((option) => <option key={option} value={option}>{option}</option>)}
    </select>
  );
}

function BooleanInput({ value, onChange }) {
  return (
    <label className="flex items-center gap-2 text-xs text-secondary-500">
      <input type="checkbox" checked={value === true} onChange={(e) => onChange(e.target.checked || undefined)} />
      {value === true ? 'true' : 'false'}
    </label>
  );
}

/** Long free text gets a textarea — a prompt or a message body in a one-line input is the
 *  single most common complaint about generated forms. */
const LONG_TEXT_HINTS = /body|content|prompt|text|message|markdown|summary|description/i;

function StringInput({ field, value, onChange }) {
  const multiline = LONG_TEXT_HINTS.test(field.name);
  if (multiline) {
    return (
      <textarea
        className={textareaClass}
        rows={4}
        value={value ?? ''}
        placeholder={field.default !== undefined ? String(field.default) : ''}
        onChange={(e) => onChange(e.target.value || undefined)}
      />
    );
  }
  return (
    <input
      className={inputClass}
      value={value ?? ''}
      placeholder={field.default !== undefined ? String(field.default) : ''}
      onChange={(e) => onChange(e.target.value || undefined)}
    />
  );
}

function ArgumentInput({ field, value, onChange }) {
  if (field.type === 'enum') return <EnumInput field={field} value={value} onChange={onChange} />;
  if (field.type === 'boolean' && !isTemplate(value)) return <BooleanInput value={value} onChange={onChange} />;
  if (field.type === 'string_array') {
    return (
      <input
        className={inputClass}
        value={Array.isArray(value) ? value.join(', ') : (value ?? '')}
        placeholder="comma-separated"
        onChange={(e) => {
          const items = e.target.value.split(',').map((s) => s.trim()).filter(Boolean);
          onChange(items.length ? items : undefined);
        }}
      />
    );
  }
  if (field.type === 'number' || field.type === 'integer') {
    return (
      <input
        className={inputClass}
        inputMode="decimal"
        value={value ?? ''}
        placeholder={field.default !== undefined ? String(field.default) : ''}
        onChange={(e) => onChange(coerceNumber(e.target.value))}
      />
    );
  }
  return <StringInput field={field} value={value} onChange={onChange} />;
}

function ArgumentRow({ field, value, onChange, upstreamIds }) {
  const label = `${field.name}${field.required ? ' *' : ''}`;
  return (
    <div className="space-y-1">
      <Field label={label} hint={field.description || undefined}>
        <ArgumentInput field={field} value={value} onChange={onChange} />
      </Field>
      {(field.type === 'string' || field.type === 'number' || field.type === 'integer') && (
        <VariableMenu
          nodeIds={upstreamIds}
          label="Insert"
          onInsert={(ref) => onChange(`${typeof value === 'string' ? value : ''}${ref}`)}
        />
      )}
    </div>
  );
}

function ModeToggleButton({ fields, raw, canRender, onToggle }) {
  if (!fields) return null;
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={raw && !canRender}
      title={raw && !canRender ? 'These arguments cannot be shown as a form' : undefined}
      className="flex items-center gap-1 text-[10px] text-primary-600 disabled:text-secondary-500 disabled:cursor-not-allowed"
    >
      <Braces size={11} />{raw ? 'Form' : 'Raw JSON'}
    </button>
  );
}

function RawJsonEditor({ value, onChange, upstreamIds, invalid, fields, canRender }) {
  return (
    <>
      <textarea
        className={textareaClass}
        rows={5}
        value={value ?? '{}'}
        onChange={(e) => onChange(e.target.value)}
      />
      {invalid && <p className="text-xs text-amber-600">{invalid}</p>}
      <VariableMenu nodeIds={upstreamIds} onInsert={(ref) => onChange(`${value || '{}'}${ref}`)} />
      <p className="text-[10px] text-secondary-500">
        Templates are substituted before parsing, so quote them:{' '}
        <code>{'{"q": "{{input.query}}"}'}</code>.
      </p>
      {fields && !canRender && (
        <p className="text-[10px] text-amber-600">
          These arguments hold a value the form cannot show (invalid JSON, or a nested
          object). Fix or simplify it to switch back.
        </p>
      )}
    </>
  );
}

function FormFieldsEditor({ fields, args, onWriteField, upstreamIds }) {
  return (
    <>
      {fields.length === 0 && <p className="text-xs text-secondary-500">This tool takes no arguments.</p>}
      {fields.map((field) => (
        field.renderable ? (
          <ArgumentRow
            key={field.name}
            field={field}
            value={args[field.name]}
            onChange={(v) => onWriteField(field.name, v)}
            upstreamIds={upstreamIds}
          />
        ) : (
          <p key={field.name} className="text-[10px] text-amber-600">
            <code>{field.name}</code> is a nested value — use Raw JSON to set it.
          </p>
        )
      ))}
      <p className="text-[10px] text-secondary-500">
        <code>*</code> marks a required parameter. Values may contain{' '}
        <code>{'{{node_id.output}}'}</code> templates.
      </p>
    </>
  );
}

function checkJsonValidity(raw, value) {
  if (!raw || !String(value ?? '').trim()) return '';
  try {
    JSON.parse(String(value));
    return '';
  } catch {
    return 'Not valid JSON yet.';
  }
}

export default function ToolArgumentsForm({ schema, value, onChange, upstreamIds = [] }) {
  const parsed = parseArguments(value);
  const fields = (schema && schema.fields) || null;
  const canRender = !!fields && !parsed.advanced && !hasUnrenderableValue(parsed.args, fields);
  const [raw, setRaw] = useState(!canRender);
  useEffect(() => { if (!canRender) setRaw(true); }, [canRender]);

  const writeField = (name, fieldValue) => {
    const next = { ...parsed.args };
    if (fieldValue === undefined) delete next[name];
    else next[name] = fieldValue;
    onChange(JSON.stringify(next));
  };

  const invalid = checkJsonValidity(raw, value);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-secondary-500">Arguments</span>
        <ModeToggleButton
          fields={fields}
          raw={raw}
          canRender={canRender}
          onToggle={() => setRaw((v) => !v)}
        />
      </div>

      {raw || !fields ? (
        <RawJsonEditor
          value={value}
          onChange={onChange}
          upstreamIds={upstreamIds}
          invalid={invalid}
          fields={fields}
          canRender={canRender}
        />
      ) : (
        <FormFieldsEditor
          fields={fields}
          args={parsed.args}
          onWriteField={writeField}
          upstreamIds={upstreamIds}
        />
      )}
    </div>
  );
}
